"use client";

import type { ReactNode } from "react";
import { GamePreviewViewport } from "./GamePreviewViewport";
import { useTranslations } from "next-intl";

type GamePreviewModalProps = {
    src: string;
    iframeTitle: string;
    onClose: () => void;
    title?: string;
    headerExtra?: ReactNode;
    closeVariant?: "neutral" | "danger";
    shellVariant?: "admin" | "editor";
};

export function GamePreviewModal({
    src,
    iframeTitle,
    onClose,
    title,
    headerExtra,
    closeVariant = "neutral",
    shellVariant = "admin",
}: GamePreviewModalProps) {
    const tMain = useTranslations("main");
    const resolvedTitle = title && title.trim() ? title : tMain("preview");
    const shellClass =
        shellVariant === "editor"
            ? "flex h-[92vh] w-full max-w-400 flex-col overflow-hidden rounded-lg bg-white shadow-[0_10px_30px_rgba(0,0,0,0.35)] sm:h-[90vh]"
            : "relative flex h-[92vh] w-full max-w-400 flex-col overflow-hidden rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.35)] sm:h-[90vh]";

    const headerClass =
        shellVariant === "editor"
            ? "flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-[#e2e2e2] px-4 py-2"
            : "flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-3";

    const titleClass =
        shellVariant === "editor"
            ? "text-base font-semibold text-[#2B2B2B]"
            : "text-base font-semibold text-neutral-800";

    const closeClass =
        closeVariant === "danger"
            ? "cursor-pointer rounded bg-[#F54B4B] px-3 py-1 text-sm font-medium text-[#ffffff] hover:bg-[#F62A2A]"
            : "cursor-pointer rounded-lg px-3 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-100";

    return (
        <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/50 p-2 sm:p-4">
            <div className={shellClass}>
                <div className={headerClass}>
                    <span className={titleClass}>{resolvedTitle}</span>
                    <div className="flex flex-wrap items-center gap-2">
                        {headerExtra}
                        <button type="button" onClick={onClose} className={closeClass}>
                            {tMain("close")}
                        </button>
                    </div>
                </div>
                <div className="min-h-0 flex-1 overflow-hidden">
                    <GamePreviewViewport
                        src={src}
                        iframeTitle={iframeTitle}
                        className="h-full min-h-0"
                    />
                </div>
            </div>
        </div>
    );
}
